/**
 * Excel (.xlsx) bulk-upload template + parser for Professional Contacts (DF155-161, Action
 * 2/3/4/5 of docs/planning/professional-contacts-bulk-upload/00-requirements.md).
 *
 * A firm's children (Individuals 1..n / Addresses 1..n / Bank Accounts 0..n — FirmUpsert.contacts
 * /.addresses/.bankAccounts) can't be flattened into one CSV row without capping the count, so the
 * template is a 4-sheet workbook (Companies/Individuals/Addresses/Bank Accounts) linked by a
 * user-assigned "Company Reference" column — the sheet shape mirrors FirmUpsert's own nesting,
 * just unpivoted into rows. See the requirements doc §2-§6 for the full field-by-field spec this
 * file implements.
 *
 * No React, no fetch — this module only knows Excel <-> the domain's FirmUpsert shape. Field
 * completeness rules are NOT re-implemented here: they're reused from professionalContacts.rules
 * (golden rule #3, no business logic scattered outside /domain).
 */
import ExcelJS from "exceljs";
import Moment from "moment";
import {
  FIRM_TYPES,
  firmTypeLabel,
} from "../../../../../domain/professionalContacts/professionalContacts.types";
import {
  validateFirmDraft,
  normaliseFirmName,
} from "../../../../../domain/professionalContacts/professionalContacts.rules";

export const SHEET = {
  INSTRUCTIONS: "Instructions",
  COMPANIES: "Companies",
  INDIVIDUALS: "Individuals",
  ADDRESSES: "Addresses",
  BANK_ACCOUNTS: "Bank Accounts",
  LISTS: "Lists",
};

const YES_NO = ["Yes", "No"];

/** Wire value -> display label, reversed (see FIRM_TYPE_LABELS) so a typed label maps back to the enum the contract expects. */
const FIRM_TYPE_LABEL_TO_VALUE = Object.fromEntries(FIRM_TYPES.map((type) => [firmTypeLabel(type), type]));

/**
 * Resolves a Companies-sheet Company Type cell (a display LABEL, e.g. "Solicitor_Lender", per
 * the FIRM_TYPE_LABELS dropdown) to its FirmType WIRE value — the same value the repository match
 * and the server key on (R12). Returns `undefined` when the cell is blank or doesn't match a known
 * label; `buildCompanySummaryRow` reports that case as a field error separately, so callers here
 * only need a stable (possibly undefined) key to group rows by, not a validity check of their own.
 * @param {string|number|""} rawType
 * @returns {string|undefined}
 */
function resolveFirmTypeValue(rawType) {
  return FIRM_TYPE_LABEL_TO_VALUE[String(rawType || "").trim()];
}

// ---------------------------------------------------------------------------------------
// Column definitions — one row per contract field. `key` matches the FirmUpsert/Contact/
// Address/BankAccount property it feeds (see requirements doc §3-§6); "Company Reference" is
// the only column with no contract field — it's stripped before the row is sent to the API.
// ---------------------------------------------------------------------------------------

export const COMPANIES_COLUMNS = [
  { key: "companyReference", header: "Company Reference*", width: 18, required: true },
  { key: "type", header: "Company Type*", width: 20, required: true, kind: "firmType" },
  { key: "name", header: "Company Name*", width: 28, required: true },
  { key: "mainPhone", header: "Main Phone Number*", width: 18, required: true },
  { key: "email", header: "Company Email*", width: 26, required: true },
  { key: "piiValue", header: "PII Value (£)", width: 14, kind: "number" },
  { key: "piiExpiry", header: "PII Expiry Date (dd/mm/yyyy)", width: 20, kind: "date" },
  { key: "panelApproved", header: "Panel Approved (Yes/No)", width: 18, kind: "yesNo" },
  { key: "panelApprovedDate", header: "Panel Approved Date (dd/mm/yyyy)", width: 20, kind: "date" },
  { key: "distressedOnly", header: "Distressed Only (Yes/No)", width: 18, kind: "yesNo" },
  { key: "active", header: "Active (Yes/No)", width: 14, kind: "yesNo" },
  { key: "noFurtherInstructions", header: "No Further Instructions (Yes/No)", width: 22, kind: "yesNo" },
  { key: "notes", header: "Notes", width: 30 },
];

export const INDIVIDUALS_COLUMNS = [
  { key: "companyReference", header: "Company Reference*", width: 18, required: true },
  { key: "name", header: "Individual Name*", width: 24, required: true },
  { key: "role", header: "Job Title / Role", width: 20 },
  { key: "email", header: "Email*", width: 26, required: true },
  { key: "phone", header: "Phone*", width: 16, required: true },
  { key: "isMain", header: "Main Contact (Yes/No)", width: 18, kind: "yesNo" },
];

export const ADDRESSES_COLUMNS = [
  { key: "companyReference", header: "Company Reference*", width: 18, required: true },
  { key: "building", header: "Building / Unit*", width: 16, required: true },
  { key: "street", header: "Street*", width: 22, required: true },
  { key: "town", header: "Town / City*", width: 18, required: true },
  { key: "postcode", header: "Postcode*", width: 12, required: true },
  { key: "country", header: "Country*", width: 18, required: true, kind: "country" },
  { key: "isPreferred", header: "Preferred Address (Yes/No)", width: 20, kind: "yesNo" },
];

export const BANK_ACCOUNTS_COLUMNS = [
  { key: "companyReference", header: "Company Reference*", width: 18, required: true },
  { key: "beneficiaryName", header: "Beneficiary Name", width: 26 },
  { key: "bankType", header: "Bank Type", width: 18, kind: "bankType" },
  { key: "accountNumber", header: "Account Number", width: 16 },
  { key: "sortCode", header: "Sort Code", width: 12 },
  { key: "bankName", header: "Creditor Bank Name", width: 24 },
  { key: "town", header: "Creditor Bank Town", width: 18 },
  { key: "postcode", header: "Creditor Bank Postcode", width: 14 },
  { key: "country", header: "Creditor Bank Country", width: 18, kind: "country" },
  { key: "address", header: "Creditor Bank Address", width: 28 },
  { key: "isMain", header: "Main Account (Yes/No)", width: 18, kind: "yesNo" },
];

const HEADER_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F2E24" } }; // HTB dark-green nav
const HEADER_FONT = { color: { argb: "FFFFFFFF" }, bold: true };
const EXAMPLE_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
const EXAMPLE_FONT = { italic: true, color: { argb: "FF666666" } };

// ---------------------------------------------------------------------------------------
// Template generation (Action 2 — "download, fill, upload from there")
// ---------------------------------------------------------------------------------------

/**
 * @param {{label: string, value: string}[]} options
 * @returns {string[]} labels only, for the dropdown / Lists sheet
 */
const labelsOf = (options) => (Array.isArray(options) ? options.map((o) => o.label).filter(Boolean) : []);

/**
 * Builds the downloadable bulk-upload workbook. `bankTypeOptions`/`countryOptions` are the app's
 * existing BankType/Country option sets (fetchBankOptionSets in ProfessionalContacts.helper.js) —
 * fetched at download time so the dropdowns never drift from the live option-set list; Company
 * Type comes from the contract-derived FIRM_TYPES/FIRM_TYPE_LABELS instead (no extra API call
 * needed, matching how the rest of this feature already sources firm types, e.g. fetchTabCounts).
 * @param {{bankTypeOptions?: Array<{label: string, value: string}>, countryOptions?: Array<{label: string, value: string}>}} [optionSets]
 * @returns {Promise<ExcelJS.Workbook>}
 */
export async function buildBulkUploadTemplate({ bankTypeOptions = [], countryOptions = [] } = {}) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Minerva Professional Contacts";
  workbook.created = new Date(0); // Date.now()/new Date() are unavailable in some hosts (workflow runs) — stamp a fixed epoch, not "now"

  const countryLabels = countryOptions.length ? labelsOf(countryOptions) : ["United Kingdom"];
  const bankTypeLabels = labelsOf(bankTypeOptions);
  const firmTypeLabels = FIRM_TYPES.map((type) => firmTypeLabel(type));

  buildListsSheet(workbook, { countryLabels, bankTypeLabels, firmTypeLabels });
  buildInstructionsSheet(workbook);
  buildDataSheet(workbook, SHEET.COMPANIES, COMPANIES_COLUMNS, {
    companyReference: "1",
    type: firmTypeLabels[0],
    name: "Example & Co LLP (delete this row)",
    mainPhone: "020 7000 0000",
    email: "info@example.co.uk",
    piiExpiry: "31/03/2027",
    panelApproved: "Yes",
    panelApprovedDate: "01/01/2025",
    distressedOnly: "No",
    active: "Yes",
    noFurtherInstructions: "No",
    notes: "Delete this example row before uploading.",
  });
  buildDataSheet(workbook, SHEET.INDIVIDUALS, INDIVIDUALS_COLUMNS, {
    companyReference: "1",
    name: "Jo Example",
    role: "Partner",
    email: "j.example@example.co.uk",
    phone: "020 7000 0001",
    isMain: "Yes",
  });
  buildDataSheet(workbook, SHEET.ADDRESSES, ADDRESSES_COLUMNS, {
    companyReference: "1",
    building: "1",
    street: "Example Street",
    town: "London",
    postcode: "EC1A 1AA",
    country: countryLabels[0],
    isPreferred: "Yes",
  });
  buildDataSheet(workbook, SHEET.BANK_ACCOUNTS, BANK_ACCOUNTS_COLUMNS, {
    companyReference: "1",
    beneficiaryName: "Example & Co LLP Client Account",
    bankType: bankTypeLabels[0] || "",
    accountNumber: "12345678",
    sortCode: "20-00-00",
    bankName: "Example Bank plc",
    town: "London",
    postcode: "E14 5HP",
    country: countryLabels[0],
    address: "1 Example Square",
    isMain: "Yes",
  });

  workbook.getWorksheet(SHEET.LISTS).state = "veryHidden";
  return workbook;
}

function buildListsSheet(workbook, { countryLabels, bankTypeLabels, firmTypeLabels }) {
  const sheet = workbook.addWorksheet(SHEET.LISTS);
  sheet.getColumn(1).values = ["Country", ...countryLabels];
  sheet.getColumn(2).values = ["BankType", ...(bankTypeLabels.length ? bankTypeLabels : ["Operations Account"])];
  sheet.getColumn(3).values = ["FirmType", ...firmTypeLabels];
  sheet.getColumn(4).values = ["YesNo", ...YES_NO];
  return sheet;
}

function listRange(column, count) {
  const lastRow = Math.max(count, 1) + 1; // +1 for the header cell in row 1
  return `${SHEET.LISTS}!$${column}$2:$${column}$${lastRow}`;
}

function buildInstructionsSheet(workbook) {
  const sheet = workbook.addWorksheet(SHEET.INSTRUCTIONS);
  sheet.getColumn(1).width = 100;
  const lines = [
    ["Professional Contacts Bulk Upload Template", true],
    ["", false],
    ["This workbook has 4 sheets to fill in: Companies, Individuals, Addresses, Bank Accounts.", false],
    ["Every company needs at least one Individuals row and one Addresses row. Bank Accounts is optional; a company can have zero.", false],
    ["", false],
    ["How the sheets link together", true],
    ["Give every row on the Companies sheet its own 'Company Reference' (e.g. 1, 2, 3; any unique text). Then use that same reference in the 'Company Reference' column on the Individuals/Addresses/Bank Accounts sheets to attach rows to that company. A company can have many Individuals/Addresses/Bank Accounts rows; just repeat its Company Reference on each one.", false],
    ["", false],
    ["Required fields", true],
    ["Columns marked with * must be filled in. Companies: Company Type, Company Name, Main Phone Number, Company Email. Individuals: Individual Name, Email, Phone (at least one complete individual per company). Addresses: Building/Unit, Street, Town/City, Postcode, Country (at least one complete address per company). Bank Accounts: if you start a row, Sort Code, Creditor Bank Name/Town/Postcode/Country/Address are all required; otherwise leave the whole row blank.", false],
    ["", false],
    ["Dropdowns", true],
    ["Company Type, Country, Bank Type and every Yes/No column are dropdowns. Click the cell and choose from the list rather than typing. A single workbook can mix different Company Types (Solicitor, Valuer, Broker, ...) across its rows.", false],
    ["", false],
    ["Dates", true],
    ["Enter dates as dd/mm/yyyy (UK format), e.g. 31/03/2027.", false],
    ["", false],
    ["Before you upload", true],
    ["Delete the example row (Company Reference '1') from every sheet once you've read it.", false],
    ["After you choose the file, this screen checks every row and shows Ready/Warning/Error per company with exactly what's missing before anything is sent.", false],
  ];
  lines.forEach(([text, heading], index) => {
    const row = sheet.getRow(index + 1);
    row.getCell(1).value = text;
    if (heading) row.getCell(1).font = { bold: true, size: text.includes("Bulk Upload Template") ? 14 : 12 };
    row.getCell(1).alignment = { wrapText: true, vertical: "top" };
  });
}

/**
 * One data sheet: styled header row (frozen), one greyed-out example row, empty rows with
 * dropdown data validation applied for 500 rows below it (Action 4 — Company Type etc. as
 * in-cell dropdowns so a mixed-type workbook can't carry an invalid value).
 */
function buildDataSheet(workbook, sheetName, columns, exampleRow) {
  const sheet = workbook.addWorksheet(sheetName);
  sheet.views = [{ state: "frozen", ySplit: 1 }];
  columns.forEach((col, index) => {
    sheet.getColumn(index + 1).width = col.width || 16;
  });

  const header = sheet.getRow(1);
  columns.forEach((col, index) => {
    const cell = header.getCell(index + 1);
    cell.value = col.header;
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
  });

  const example = sheet.getRow(2);
  columns.forEach((col, index) => {
    const cell = example.getCell(index + 1);
    cell.value = exampleRow[col.key] ?? "";
    cell.fill = EXAMPLE_FILL;
    cell.font = EXAMPLE_FONT;
  });

  const DATA_ROWS = 500;
  const firstDataRow = 3;
  const lastDataRow = firstDataRow + DATA_ROWS - 1;
  columns.forEach((col, index) => {
    if (!col.kind || col.kind === "number") return;
    const formulae = dropdownFormulaeFor(workbook, col);
    if (!formulae) return;
    for (let r = firstDataRow; r <= lastDataRow; r += 1) {
      sheet.getCell(r, index + 1).dataValidation = {
        type: "list",
        allowBlank: !col.required,
        formulae,
        showErrorMessage: true,
        errorStyle: "error",
        errorTitle: "Invalid value",
        error: `Please choose a value from the dropdown list for "${col.header}".`,
      };
    }
  });

  return sheet;
}

function dropdownFormulaeFor(workbook, col) {
  switch (col.kind) {
    case "yesNo":
      return [listRange("D", YES_NO.length)];
    case "firmType":
      return [listRange("C", FIRM_TYPES.length)];
    case "bankType": {
      const bankTypeCount = workbook.getWorksheet(SHEET.LISTS).getColumn(2).values.length - 2; // minus header label + values[0] offset
      return bankTypeCount > 0 ? [listRange("B", bankTypeCount)] : null;
    }
    case "country": {
      const countryCount = workbook.getWorksheet(SHEET.LISTS).getColumn(1).values.length - 2;
      return countryCount > 0 ? [listRange("A", countryCount)] : null;
    }
    default:
      return null;
  }
}

/**
 * Builds the template and triggers a browser download — no server round-trip (Action 2).
 * @param {{bankTypeOptions?: Array<{label: string, value: string}>, countryOptions?: Array<{label: string, value: string}>}} [optionSets]
 * @param {string} [fileName]
 */
export async function downloadBulkUploadTemplate(optionSets, fileName = "professional-contacts-bulk-upload-template.xlsx") {
  const workbook = await buildBulkUploadTemplate(optionSets);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}

// ---------------------------------------------------------------------------------------
// Parsing an uploaded workbook back into rows (Action 3/5)
// ---------------------------------------------------------------------------------------

/**
 * @param {ExcelJS.Worksheet} sheet
 * @param {Array<{key: string, header: string}>} columns
 * @returns {Array<Object>} one object per non-blank row, plus `_row` (1-based Excel row number)
 */
function readSheetRows(sheet, columns) {
  if (!sheet) return [];
  const headerRow = sheet.getRow(1);
  const columnIndexByKey = {};
  columns.forEach((col) => {
    for (let c = 1; c <= headerRow.cellCount; c += 1) {
      const headerText = String(headerRow.getCell(c).value || "").trim();
      if (headerText === col.header) {
        columnIndexByKey[col.key] = c;
        break;
      }
    }
  });

  const rows = [];
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber <= 2) return; // header + example row
    const values = {};
    let hasAnyValue = false;
    columns.forEach((col) => {
      const idx = columnIndexByKey[col.key];
      const raw = idx ? row.getCell(idx).value : null;
      const value = normaliseCellValue(raw);
      values[col.key] = value;
      if (value !== null && value !== "") hasAnyValue = true;
    });
    if (hasAnyValue) rows.push({ _row: rowNumber, ...values });
  });
  return rows;
}

function normaliseCellValue(raw) {
  if (raw === null || raw === undefined) return "";
  if (raw instanceof Date) return raw;
  if (typeof raw === "object" && raw.result !== undefined) return normaliseCellValue(raw.result); // formula cell
  if (typeof raw === "object" && raw.text !== undefined) return String(raw.text).trim(); // rich text
  if (typeof raw === "number") return raw;
  return String(raw).trim();
}

/**
 * Reads the uploaded .xlsx into its 4 raw row sets. Throws if the Companies sheet (the only
 * strictly-required one) is missing — everything else is treated as an empty sheet so a workbook
 * with no Bank Accounts rows still parses fine (a firm may have zero, per the contract).
 * @param {ArrayBuffer} arrayBuffer
 */
export async function parseBulkUploadWorkbook(arrayBuffer) {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  const companiesSheet = workbook.getWorksheet(SHEET.COMPANIES);
  if (!companiesSheet) {
    throw new Error(`This file has no "${SHEET.COMPANIES}" sheet. Please use the downloaded template.`);
  }

  return {
    companies: readSheetRows(companiesSheet, COMPANIES_COLUMNS),
    individuals: readSheetRows(workbook.getWorksheet(SHEET.INDIVIDUALS), INDIVIDUALS_COLUMNS),
    addresses: readSheetRows(workbook.getWorksheet(SHEET.ADDRESSES), ADDRESSES_COLUMNS),
    bankAccounts: readSheetRows(workbook.getWorksheet(SHEET.BANK_ACCOUNTS), BANK_ACCOUNTS_COLUMNS),
  };
}

// ---------------------------------------------------------------------------------------
// Row -> FirmUpsert draft assembly + client-side validation summary (Action 5)
// ---------------------------------------------------------------------------------------

/** @param {string|Date|""} value @returns {boolean|undefined} */
function readYesNo(value) {
  if (value === "" || value === null || value === undefined) return undefined;
  const text = String(value).trim().toLowerCase();
  if (text === "yes") return true;
  if (text === "no") return false;
  return null; // signals "present but invalid"
}

/** @param {string|Date|""} value @returns {string|null|undefined} ISO yyyy-mm-dd, null if unparsable, undefined if blank */
function readDate(value) {
  if (value === "" || value === null || value === undefined) return undefined;
  const moment = value instanceof Date ? Moment(value) : Moment(String(value).trim(), ["DD/MM/YYYY", "D/M/YYYY", "YYYY-MM-DD"], true);
  return moment.isValid() ? moment.format("YYYY-MM-DD") : null;
}

/** @param {string|number|""} value @returns {number|null|undefined} */
function readNumber(value) {
  if (value === "" || value === null || value === undefined) return undefined;
  if (typeof value === "number") return value;
  // Only strip currency symbols / thousands separators, never letters — "N/A" or "abc" must fail
  // validation, not silently strip down to "" (which Number("") reads as a "valid" 0).
  const cleaned = String(value).trim().replace(/[£$€,\s]/g, "");
  if (cleaned === "") return null;
  const n = Number(cleaned);
  return Number.isNaN(n) ? null : n;
}

/**
 * Coerces any cell value (string, number, or blank) to a trimmed string for a text field — a
 * fully-numeric postcode/account-number/etc. reads back from Excel as a JS `number`, and the
 * domain rules' `.trim()` completeness checks would throw on a non-string, not just misreport it.
 * @param {string|number|""|null|undefined} value
 * @returns {string}
 */
function toText(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

/**
 * Groups the 4 raw row sets by Company Reference, converts each company's rows into a
 * FirmUpsert-shaped draft, validates every draft with the SAME rules the single-firm Add/Edit
 * form uses (professionalContacts.rules.validateFirmDraft — golden rule #3), and returns one
 * summary row per company (Ready/Warning/Error) plus a bucket of "orphan" child rows that
 * reference a Company Reference no Companies row declared.
 *
 * This mirrors the BulkResult shape the API returns from POST /professional-firms/bulk
 * (rowNumber/companyName/status/message) with the richer per-field `fieldErrors` + `companyReference`
 * proposed in docs/planning/professional-contacts-bulk-upload/00-requirements.md §9.2, so the same
 * table can render this client-side pre-check now and the server's response after submit.
 *
 * @param {{companies: Object[], individuals: Object[], addresses: Object[], bankAccounts: Object[]}} parsed
 * @param {{bankTypeLabelToValue?: Object<string,string>, countryLabelToValue?: Object<string,string>}} [labelMaps]
 * @returns {{ rows: Array<{rowNumber:number, companyReference:string, companyName:string, type?:string, status:"Ready"|"Warning"|"Error", message:string, fieldErrors:{field:string,message:string}[], firmUpsert:?Object}>, totalRows:number, readyCount:number, warningCount:number, errorCount:number }}
 */
export function buildBulkUploadSummary({ companies, individuals, addresses, bankAccounts }, labelMaps = {}) {
  const byReference = new Map();
  const rows = [];
  const seenReferences = new Set();

  companies.forEach((row) => {
    const ref = String(row.companyReference || "").trim();
    if (!ref) {
      rows.push(errorRow(row._row, "", "(missing Company Reference)", [
        { field: `Companies[row ${row._row}].Company Reference`, message: "Company Reference is required" },
      ]));
      return;
    }
    if (seenReferences.has(ref)) {
      rows.push(errorRow(row._row, ref, toText(row.name), [
        { field: `Companies[row ${row._row}].Company Reference`, message: `Duplicate Company Reference "${ref}" must be unique` },
      ]));
      return;
    }
    seenReferences.add(ref);
    byReference.set(ref, { companyRow: row, contacts: [], addresses: [], bankAccounts: [] });
  });

  const attachChildren = (childRows, sheetName, targetKey, mapRow) => {
    childRows.forEach((row) => {
      const ref = String(row.companyReference || "").trim();
      const bucket = byReference.get(ref);
      if (!bucket) {
        rows.push(
          errorRow(row._row, ref, "(unknown company)", [
            {
              field: `${sheetName}[row ${row._row}].Company Reference`,
              message: ref
                ? `Company Reference "${ref}" was not found on the ${SHEET.COMPANIES} sheet`
                : "Company Reference is required",
            },
          ])
        );
        return;
      }
      bucket[targetKey].push(mapRow(row));
    });
  };

  attachChildren(individuals, SHEET.INDIVIDUALS, "contacts", (row) => ({
    name: toText(row.name),
    role: toText(row.role),
    email: toText(row.email),
    phone: toText(row.phone),
    isMain: readYesNo(row.isMain) === true,
    _row: row._row,
    _isMainRaw: readYesNo(row.isMain),
  }));
  attachChildren(addresses, SHEET.ADDRESSES, "addresses", (row) => ({
    building: toText(row.building),
    street: toText(row.street),
    town: toText(row.town),
    postcode: toText(row.postcode),
    country: resolveLabel(toText(row.country), labelMaps.countryLabelToValue),
    isPreferred: readYesNo(row.isPreferred) === true,
    _row: row._row,
    _isPreferredRaw: readYesNo(row.isPreferred),
  }));
  attachChildren(bankAccounts, SHEET.BANK_ACCOUNTS, "bankAccounts", (row) => ({
    beneficiaryName: toText(row.beneficiaryName),
    bankType: resolveLabel(toText(row.bankType), labelMaps.bankTypeLabelToValue),
    accountNumber: toText(row.accountNumber),
    sortCode: toText(row.sortCode),
    bankName: toText(row.bankName),
    town: toText(row.town),
    postcode: toText(row.postcode),
    country: resolveLabel(toText(row.country), labelMaps.countryLabelToValue),
    address: toText(row.address),
    isMain: readYesNo(row.isMain) === true,
    _row: row._row,
    _isMainRaw: readYesNo(row.isMain),
  }));

  // Duplicate Company Name WITHIN this workbook — distinct from Company Reference above, and
  // distinct from a duplicate against the existing REPOSITORY, which the parser cannot judge on
  // its own: see markExistingCompanies below, which the component feeds with the repository's
  // names. Importable (Warning), not blocked: the server creates the first occurrence and reports
  // the second as Duplicate (R9), so the file still yields one company.
  //
  // Normalisation is normaliseFirmName (R1) — the SAME helper the against-repository match and
  // the Add/Edit form use, so the two checks cannot drift apart.
  //
  // v1.14.0 (FIRM-DUPLICATE-NAMES-015, R12): keyed on (FirmTypeCode, name), not name alone — two
  // rows sharing a name under DIFFERENT types are no longer duplicates of each other, matching the
  // server. A nested Map (type -> name -> rows), not a concatenated string key: same composite-key
  // caution as FIRM-DUPLICATE-NAMES-013's C# implementation ("Broker" + "X Ltd" must never collide
  // with "BrokerX" + " Ltd").
  const rowNumbersByTypeAndName = new Map();
  byReference.forEach((bucket, ref) => {
    const typeValue = resolveFirmTypeValue(bucket.companyRow.type);
    const normalised = normaliseFirmName(bucket.companyRow.name);
    if (!normalised) return;
    if (!rowNumbersByTypeAndName.has(typeValue)) rowNumbersByTypeAndName.set(typeValue, new Map());
    const byName = rowNumbersByTypeAndName.get(typeValue);
    if (!byName.has(normalised)) byName.set(normalised, []);
    byName.get(normalised).push({ ref, row: bucket.companyRow._row });
  });

  byReference.forEach((bucket, ref) => {
    const typeValue = resolveFirmTypeValue(bucket.companyRow.type);
    const normalised = normaliseFirmName(bucket.companyRow.name);
    const duplicateNameRows = (rowNumbersByTypeAndName.get(typeValue)?.get(normalised) || []).filter(
      (d) => d.ref !== ref
    );
    rows.push(buildCompanySummaryRow(ref, bucket, duplicateNameRows));
  });

  rows.sort((a, b) => a.rowNumber - b.rowNumber);

  return countSummary(rows);
}

/**
 * The message a row carries once it is known to already exist in the repository (R11).
 *
 * v1.14.0 (FIRM-DUPLICATE-NAMES-015, R12): names the firm TYPE as well as the company, for the
 * same reason FIRM-DUPLICATE-NAMES-013's bulk API message does — two rows with the same name and
 * different types are no longer duplicates, so a message that doesn't say which type collided is
 * ambiguous. `typeValue` is the WIRE value (matching); the DISPLAY label is shown.
 * @param {string|undefined} typeValue
 * @returns {string}
 */
export function buildAlreadyInRepositoryMessage(typeValue) {
  return `Already in the repository as ${firmTypeLabel(typeValue)} — this row will not be created.`;
}

/** Totals for a set of summary rows. One definition, so a recount cannot disagree with the original count. */
function countSummary(rows) {
  return {
    rows,
    totalRows: rows.length,
    readyCount: rows.filter((r) => r.status === "Ready").length,
    warningCount: rows.filter((r) => r.status === "Warning").length,
    errorCount: rows.filter((r) => r.status === "Error").length,
    duplicateCount: rows.filter((r) => r.status === "Duplicate").length,
  };
}

/**
 * Marks every row whose Company Name already exists in the repository as "Duplicate" (R7), so the
 * user sees what will be skipped BEFORE pressing Import rather than in the result afterwards.
 *
 * Pure, and deliberately here rather than in the component: the rest of the row-shaping logic and
 * its tests live in this module.
 *
 * The repository's names arrive from ONE request per uploaded file (R10) — matching happens in
 * memory against this set, never one lookup per row.
 *
 * Precedence matches the server's (FIRM-DUPLICATE-NAMES-004): an Error row stays an Error, because
 * a row the user must fix is more actionable than one that merely already exists. Only otherwise
 * importable rows (Ready, or Warning for an in-file repeat) become Duplicate.
 *
 * Advisory only. It can go stale between parse and import, which is exactly why the server skips
 * duplicates too and why the post-import table still reports them.
 *
 * @param {Object} summary the output of buildBulkUploadSummary
 * @param {Array<{id?: string, name?: string, type?: string}>} existingFirms every firm currently in the repository
 * @returns {Object} a NEW summary; the input is not mutated
 */
export function markExistingCompanies(summary, existingFirms) {
  if (!summary || !Array.isArray(summary.rows)) return summary;

  // First firm wins on a name held by two firms of the SAME type — the repository has no unique
  // index, so that is a real case, and it mirrors usp_ProfessionalFirm_FindByName's deterministic
  // TOP 1.
  //
  // v1.14.0 (FIRM-DUPLICATE-NAMES-015, R12): keyed on (FirmTypeCode, name) via a nested Map, same
  // composite-key reasoning as the in-file check above and FIRM-DUPLICATE-NAMES-013's C#
  // implementation — never a concatenated `type + "|" + name` string.
  const firmIdByTypeAndName = new Map();
  (existingFirms || []).forEach((firm) => {
    const key = normaliseFirmName(firm?.name);
    if (!key) return;
    const type = firm?.type;
    if (!firmIdByTypeAndName.has(type)) firmIdByTypeAndName.set(type, new Map());
    const byName = firmIdByTypeAndName.get(type);
    if (!byName.has(key)) byName.set(key, firm?.id ?? null);
  });

  if (firmIdByTypeAndName.size === 0) return summary;

  const rows = summary.rows.map((row) => {
    if (row.status === "Error") return row;
    const key = normaliseFirmName(row.companyName);
    const byName = firmIdByTypeAndName.get(row.type);
    if (!key || !byName || !byName.has(key)) return row;
    return {
      ...row,
      status: "Duplicate",
      existingFirmId: byName.get(key),
      message: buildAlreadyInRepositoryMessage(row.type),
    };
  });

  return countSummary(rows);
}

function resolveLabel(label, labelToValue) {
  if (!label) return "";
  if (!labelToValue) return label;
  return Object.prototype.hasOwnProperty.call(labelToValue, label) ? labelToValue[label] : label;
}

function errorRow(rowNumber, companyReference, companyName, fieldErrors) {
  return {
    rowNumber,
    companyReference,
    companyName,
    status: "Error",
    message: fieldErrors[0]?.message || "Invalid row",
    fieldErrors,
    firmUpsert: null,
  };
}

function buildCompanySummaryRow(ref, bucket, duplicateNameRows = []) {
  const { companyRow, contacts, addresses, bankAccounts } = bucket;
  const fieldErrors = [];
  const warnings = [];

  // Resolved BEFORE the in-file duplicate-name warning below so that warning can name the type
  // (R12 — with types in play, "also appears on row N" alone is ambiguous about which types
  // collided).
  const typeValue = resolveFirmTypeValue(companyRow.type);

  if (duplicateNameRows.length) {
    const otherRows = duplicateNameRows.map((d) => d.row).join(", ");
    const typeLabel = typeValue ? firmTypeLabel(typeValue) : toText(companyRow.type);
    warnings.push(
      `Company Name "${toText(companyRow.name)}" also appears on Companies row ${otherRows} in this file for ${typeLabel}. Check these aren't the same company`
    );
  }

  if (companyRow.type && !typeValue) {
    fieldErrors.push({
      field: `Companies[row ${companyRow._row}].Company Type`,
      message: `"${companyRow.type}" is not a valid Company Type. Choose one from the dropdown`,
    });
  }

  const piiExpiry = readDate(companyRow.piiExpiry);
  if (piiExpiry === null) {
    fieldErrors.push({ field: `Companies[row ${companyRow._row}].PII Expiry Date`, message: "PII Expiry Date must be a valid dd/mm/yyyy date" });
  }
  const panelApprovedDate = readDate(companyRow.panelApprovedDate);
  if (panelApprovedDate === null) {
    fieldErrors.push({ field: `Companies[row ${companyRow._row}].Panel Approved Date`, message: "Panel Approved Date must be a valid dd/mm/yyyy date" });
  }
  const piiValue = readNumber(companyRow.piiValue);
  if (piiValue === null) {
    fieldErrors.push({ field: `Companies[row ${companyRow._row}].PII Value`, message: "PII Value must be a number" });
  }

  const panelApproved = readYesNo(companyRow.panelApproved);
  const distressedOnly = readYesNo(companyRow.distressedOnly);
  const active = readYesNo(companyRow.active);
  const noFurtherInstructions = readYesNo(companyRow.noFurtherInstructions);
  [
    ["Panel Approved", panelApproved],
    ["Distressed Only", distressedOnly],
    ["Active", active],
    ["No Further Instructions", noFurtherInstructions],
  ].forEach(([label, parsed]) => {
    if (parsed === null) {
      fieldErrors.push({ field: `Companies[row ${companyRow._row}].${label}`, message: `${label} must be "Yes" or "No"` });
    }
  });

  if (panelApproved === true && !panelApprovedDate) {
    warnings.push(`Panel Approved Date is recommended when Panel Approved is "Yes" (row ${companyRow._row})`);
  }

  const mainFlaggedContacts = contacts.filter((c) => c._isMainRaw === true);
  if (mainFlaggedContacts.length > 1) {
    warnings.push(`More than one individual marked "Main Contact". Using row ${mainFlaggedContacts[0]._row}`);
  }
  const preferredFlaggedAddresses = addresses.filter((a) => a._isPreferredRaw === true);
  if (preferredFlaggedAddresses.length > 1) {
    warnings.push(`More than one address marked "Preferred". Using row ${preferredFlaggedAddresses[0]._row}`);
  }
  const mainFlaggedBankAccounts = bankAccounts.filter((b) => b._isMainRaw === true);
  if (mainFlaggedBankAccounts.length > 1) {
    warnings.push(`More than one bank account marked "Main Account". Using row ${mainFlaggedBankAccounts[0]._row}`);
  }
  contacts.forEach((c) => {
    if (c._isMainRaw === null) fieldErrors.push({ field: `Individuals[row ${c._row}].Main Contact`, message: 'Main Contact must be "Yes" or "No"' });
  });
  addresses.forEach((a) => {
    if (a._isPreferredRaw === null) fieldErrors.push({ field: `Addresses[row ${a._row}].Preferred Address`, message: 'Preferred Address must be "Yes" or "No"' });
  });
  bankAccounts.forEach((b) => {
    if (b._isMainRaw === null) fieldErrors.push({ field: `Bank Accounts[row ${b._row}].Main Account`, message: 'Main Account must be "Yes" or "No"' });
  });
  // If none was flagged main/preferred, the first row wins by the same fallback the domain layer
  // (getMainContact/getPreferredAddress) already applies — no warning needed for that case.
  if (mainFlaggedContacts.length === 0 && contacts.length) contacts[0].isMain = true;
  if (preferredFlaggedAddresses.length === 0 && addresses.length) addresses[0].isPreferred = true;
  if (mainFlaggedBankAccounts.length === 0 && bankAccounts.length) bankAccounts[0].isMain = true;

  const draft = {
    type: typeValue || toText(companyRow.type),
    name: toText(companyRow.name),
    mainPhone: toText(companyRow.mainPhone),
    email: toText(companyRow.email),
    piiValue: piiValue || undefined,
    piiExpiry: piiExpiry || undefined,
    panelApproved: panelApproved === true,
    panelApprovedDate: panelApprovedDate || undefined,
    distressedOnly: distressedOnly === true,
    active: active === false ? false : true,
    noFurtherInstructions: noFurtherInstructions === true,
    notes: toText(companyRow.notes),
    // eslint-disable-next-line no-unused-vars
    contacts: contacts.map(({ _row, _isMainRaw, ...c }) => c),
    // eslint-disable-next-line no-unused-vars
    addresses: addresses.map(({ _row, _isPreferredRaw, ...a }) => a),
    // eslint-disable-next-line no-unused-vars
    bankAccounts: bankAccounts.map(({ _row, _isMainRaw, ...b }) => b),
  };

  const { missing } = validateFirmDraft(draft);
  missing.forEach((m) => fieldErrors.push({ field: `Company "${ref}"`, message: m }));

  const status = fieldErrors.length > 0 ? "Error" : warnings.length > 0 ? "Warning" : "Ready";
  const message =
    fieldErrors.length > 0
      ? `${fieldErrors.length} field${fieldErrors.length === 1 ? "" : "s"} need attention`
      : warnings.length > 0
      ? warnings[0]
      : "Ready to import";

  return {
    rowNumber: companyRow._row,
    companyReference: ref,
    companyName: toText(companyRow.name),
    // v1.14.0 (FIRM-DUPLICATE-NAMES-015, R12): the WIRE value, so markExistingCompanies can key
    // its repository match on (type, name) without re-deriving the type from a display label.
    // `undefined` for a blank/invalid Company Type — such a row is already an Error (see the
    // field-error check above) and is never consulted for duplicates (see markExistingCompanies).
    type: typeValue,
    status,
    message,
    fieldErrors: [...fieldErrors, ...warnings.map((w) => ({ field: `Company "${ref}"`, message: w }))],
    firmUpsert: status === "Error" ? null : draft,
  };
}
